import React, { useEffect, useState, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import "../index.css";
import { cs } from "date-fns/locale";
import { Eye, CheckCircle, RotateCcw, XCircle, X } from "lucide-react";

const initialDetailState = {
  open: false,
  request: null,
  calendarLoading: false,
  calendarError: "",
  approvedDays: [],
  pendingDays: [],
  calendarMonth: new Date(),
  hoverInfo: null,
};

// jednotný formát dd.mm.yyyy
function fmt(d) {
  const dd = d.getDate().toString().padStart(2, "0");
  const mm = (d.getMonth() + 1).toString().padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}.${mm}.${yyyy}`;
}

// normalizace na půlnoc kvůli porovnávání data
function normalizeDate(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

// Sladší formátování dnů – intervaly (dd.mm.yyyy–dd.mm.yyyy)
function formatDaysInterval(days) {
  if (!Array.isArray(days) || days.length === 0) return "";

  const sorted = [...days]
    .map((d) => new Date(d.date))
    .sort((a, b) => a - b);

  const intervals = [];
  let start = sorted[0];
  let prev = sorted[0];

  for (let i = 1; i < sorted.length; i++) {
    const cur = sorted[i];
    const diffDays = Math.round((cur - prev) / 86400000);

    if (diffDays === 1) {
      prev = cur;
    } else {
      intervals.push([start, prev]);
      start = cur;
      prev = cur;
    }
  }
  intervals.push([start, prev]);

  return intervals
    .map(([s, e]) => {
      if (s.toDateString() === e.toDateString()) {
        return fmt(s);
      }
      return `${fmt(s)}–${fmt(e)}`;
    })
    .join("; ");
}

function formatPharmacy(code) {
  if (!code) return "-";
  if (code === "FLY") return "Létající";
  return code;
}

function statusColor(status) {
  if (!status) return "blue";

  if (status === "schváleno" || status === "schváleno (oprava)") {
    return "green";
  }
  if (status === "zamítnuto" || status === "zamítnuto (oprava)") {
    return "red";
  }
  if (status === "vráceno") {
    return "orange";
  }
  if (status === "zrušeno") {
    return "gray";
  }
  return "blue";
}

/* ------------------------------------------------------------
   Svátky (ČR) – lokálně, aby se chovaly stejně jako na žádosti
------------------------------------------------------------ */

// Výpočet Velikonoc (Meeus/Jones/Butcher) – vrací neděli
function easterSunday(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;

  return new Date(year, month - 1, day);
}

function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

function getCzechHolidaysForYear(year) {
  const fixed = [
    [0, 1],
    [4, 1],
    [4, 8],
    [6, 5],
    [6, 6],
    [8, 28],
    [9, 28],
    [10, 17],
    [11, 24],
    [11, 25],
    [11, 26],
  ].map(([m, d]) => new Date(year, m, d));

  const easter = easterSunday(year);
  const goodFriday = addDays(easter, -2);
  const easterMonday = addDays(easter, 1);

  return [goodFriday, easterMonday, ...fixed].map(normalizeDate);
}

function formatLeaveType(t) {
  switch (t) {
    case "dovolena":
      return "Dovolená";
    case "nahradni-volno":
      return "Náhradní volno";
    case "lekar":
      return "Lékař";
    case "jine":
      return "Jiné";
    case "celodenni":
      return "Celodenní směna";
    case "ranni":
      return "Ranní směna";
    case "odpo":
      return "Odpolední směna";
    default:
      return t;
  }
}

export default function ApproveLeavesPage() {
  const { user, loading: authLoading } = useAuth();

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const detailCalendarRef = useRef(null);

  // Modal pro zamítnutí / vrácení
  const [modal, setModal] = useState({
    open: false,
    mode: null, // "reject" | "return"
    requestId: null,
    note: "",
  });

  // Detail žádosti + kalendář pobočky
  const [detail, setDetail] = useState(initialDetailState);

  // Svátky pro detail kalendář
  const [holidays, setHolidays] = useState([]);

  const isAdmin = user?.role === "admin";
  const isRM = user?.role === "rm";

  /* ------------------------------------------------------------
     Načtení čekajících žádostí pro firmu
  ------------------------------------------------------------ */
  async function loadRequests() {
    if (!user) return;
    if (!isAdmin && !isRM) return;

    setLoading(true);

    try {
      const res = await fetch(`/api/leaves/firma/${user.firma}/pending`, {
        credentials: "include",
      });
      const data = await res.json();

      const list = Array.isArray(data) ? data : [];
      list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      setRequests(list);
    } catch (err) {
      console.error("Error loading leave requests:", err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!user) return;
    loadRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  /* ------------------------------------------------------------
     Akce: schválit
  ------------------------------------------------------------ */
  async function approve(id) {
    const req = requests.find((r) => r._id === id);

    let question = "Opravdu schválit tuto žádost?";
    if (req?.metaLabel === "Zrušení schválené žádosti") {
      question = "Opravdu schválit ZRUŠENÍ schválené žádosti?";
    } else if (req?.metaLabel === "Oprava schválené žádosti") {
      question = "Opravdu schválit OPRAVU schválené žádosti?";
    }

    if (!window.confirm(question)) return;

    try {
      const res = await fetch(`/api/leaves/${id}/approve`, {
        method: "POST",
        credentials: "include",
      });

      let j = null;
      try {
        j = await res.json();
      } catch {}

      if (!res.ok) {
        throw new Error(j?.message || "Chyba při schvalování.");
      }

      await loadRequests();
    } catch (err) {
      alert(err.message || "Chyba při schvalování.");
      console.error(err);
    }
  }

  /* ------------------------------------------------------------
     Akce: zamítnout / vrátit
  ------------------------------------------------------------ */
  function openReject(id) {
    setModal({ open: true, mode: "reject", requestId: id, note: "" });
  }

  function openReturn(id) {
    setModal({ open: true, mode: "return", requestId: id, note: "" });
  }

  async function submitModal() {
    try {
      const endpoint =
        modal.mode === "reject"
          ? `/api/leaves/${modal.requestId}/reject`
          : `/api/leaves/${modal.requestId}/return`;

      const res = await fetch(endpoint, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ note: modal.note }),
      });

      let j = null;
      try {
        j = await res.json();
      } catch {}

      if (!res.ok) {
        throw new Error(j?.message || "Chyba při odesílání.");
      }

      setModal({ open: false, mode: null, requestId: null, note: "" });
      await loadRequests();
    } catch (err) {
      alert(err.message || "Chyba při odesílání.");
      console.error(err);
    }
  }

  /* ------------------------------------------------------------
     Detail žádosti + kalendář pobočky
  ------------------------------------------------------------ */
  function closeDetail() {
    setDetail(initialDetailState);
  }

  async function openDetail(req) {
    if (!user) return;

    const firstDate =
      req.days && req.days.length ? new Date(req.days[0].date) : new Date();

    const y = firstDate.getFullYear();
    setHolidays([
      ...getCzechHolidaysForYear(y - 1),
      ...getCzechHolidaysForYear(y),
      ...getCzechHolidaysForYear(y + 1),
    ]);

    setDetail({
      open: true,
      request: req,
      calendarLoading: true,
      calendarError: "",
      approvedDays: [],
      pendingDays: [],
      calendarMonth: firstDate,
      hoverInfo: null,
    });

    try {
      const res = await fetch(`/api/leaves/${user.firma}/${req.pharmacyCode}`, {
        credentials: "include",
      });

      if (!res.ok) {
        let msg = `Chyba načítání kalendáře (${res.status})`;
        try {
          const j = await res.json();
          if (j?.message) msg = j.message;
        } catch {}
        throw new Error(msg);
      }

      const data = await res.json();
      const list = Array.isArray(data) ? data : [];

      const approved = [];
      const pending = [];

      for (const r of list) {
        for (const d of r.days || []) {
          const ts = new Date(d.date).setHours(0, 0, 0, 0);

          const dayObj = {
            ts,
            requestId: r._id,
            type: d.type,
            hours: d.hours,
            employeeId:
              typeof r.employeeId === "string" ? r.employeeId : r.employeeId?._id,
            employeeName:
              r.employeeName ||
              r.employeeFullName ||
              r.employee?.fullName ||
              r.employeeId?.fullName ||
              "",
            status: r.status,
          };

          const isApprovedEffective =
            r.status === "schváleno" || r.status === "schváleno (oprava)";
          const isPending = r.status === "čeká";

          if (isApprovedEffective) {
            approved.push(dayObj);
          }
          if (isPending) {
            pending.push(dayObj);
          }
        }
      }

      setDetail((prev) => ({
        ...prev,
        calendarLoading: false,
        calendarError: "",
        approvedDays: approved,
        pendingDays: pending,
      }));
    } catch (err) {
      console.error("Error loading branch calendar:", err);
      setDetail((prev) => ({
        ...prev,
        calendarLoading: false,
        calendarError: err?.message || "Chyba při načítání kalendáře.",
      }));
    }
  }

  function showDetailHoverInfo(date, rect) {
    const ts = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    ).getTime();

    const pendingForDay = detail.pendingDays.filter((d) => d.ts === ts);
    const approvedForDay = detail.approvedDays.filter((d) => d.ts === ts);

    if (pendingForDay.length === 0 && approvedForDay.length === 0) {
      setDetail((prev) => ({ ...prev, hoverInfo: null }));
      return;
    }

    setDetail((prev) => ({
      ...prev,
      hoverInfo: {
        x: rect.left + rect.width + 8,
        y: rect.top - 2,
        date,
        pending: pendingForDay,
        approved: approvedForDay,
      },
    }));
  }

  useEffect(() => {
    const root = detailCalendarRef.current;
    if (!root || !detail.open || detail.calendarLoading || detail.calendarError) {
      return;
    }

    let lastDay = null;

    const handleMove = (e) => {
      const cell = e.target.closest(".rdp-day");
      if (!cell || !root.contains(cell)) {
        if (lastDay !== null) {
          setDetail((prev) => ({ ...prev, hoverInfo: null }));
          lastDay = null;
        }
        return;
      }

      const dayStr = cell.getAttribute("data-day");
      if (!dayStr || dayStr === lastDay) return;

      lastDay = dayStr;

      const date = new Date(dayStr);
      const rect = cell.getBoundingClientRect();
      showDetailHoverInfo(date, rect);
    };

    const handleLeave = () => {
      lastDay = null;
      setDetail((prev) => ({ ...prev, hoverInfo: null }));
    };

    root.addEventListener("mousemove", handleMove);
    root.addEventListener("mouseleave", handleLeave);

    return () => {
      root.removeEventListener("mousemove", handleMove);
      root.removeEventListener("mouseleave", handleLeave);
    };
  }, [
    detail.open,
    detail.calendarLoading,
    detail.calendarError,
    detail.approvedDays,
    detail.pendingDays,
  ]);

  /* ------------------------------------------------------------
     UI – modal pro odmítnutí / vrácení
  ------------------------------------------------------------ */
  function renderReasonModal() {
    if (!modal.open) return null;

    const req = requests.find((r) => r._id === modal.requestId);
    const isCorrection =
      req?.metaLabel === "Oprava schválené žádosti" ||
      req?.metaLabel === "Zrušení schválené žádosti";

    const title =
      modal.mode === "reject"
        ? isCorrection
          ? "Zamítnout opravnou žádost"
          : "Zamítnout žádost"
        : isCorrection
          ? "Vrátit opravnou žádost k přepracování"
          : "Vrátit žádost k přepracování";

    return (
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
        <div className="bg-white p-6 rounded-lg shadow-md w-full max-w-md space-y-4">
          <h2 className="text-lg font-bold">{title}</h2>

          <textarea
            className="w-full border p-2 rounded min-h-[120px]"
            placeholder="Poznámka (nepovinné)"
            value={modal.note}
            onChange={(e) => setModal((m) => ({ ...m, note: e.target.value }))}
          />

          <div className="flex justify-end gap-2">
            <button
              className="px-3 py-1 text-sm rounded-full border border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
              onClick={() =>
                setModal({
                  open: false,
                  mode: null,
                  requestId: null,
                  note: "",
                })
              }
            >
              Zavřít
            </button>
            <button
              className="px-3 py-1 text-sm rounded-full border border-blue-600 text-blue-700 bg-blue-50 hover:bg-blue-100"
              onClick={submitModal}
            >
              Odeslat
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ------------------------------------------------------------
     UI – detailový modal s kalendářem
  ------------------------------------------------------------ */
  function renderDetailModal() {
    if (!detail.open || !detail.request) return null;

    const req = detail.request;

    return (
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-40">
        <div className="bg-white p-6 rounded-lg shadow-md w-full max-w-5xl space-y-4">
          <div className="flex justify-between items-start gap-4">
            <div>
              <h2 className="text-xl font-bold mb-1">
                Žádost o volno – {req.employeeName}
              </h2>
              <p className="text-sm text-gray-600">
                Pobočka:{" "}
                <span className="font-semibold">
                  {formatPharmacy(req.pharmacyCode)}
                </span>
              </p>
              {req.metaLabel && (
                <p className="text-xs text-purple-700 mt-1">
                  Typ: <span className="font-semibold">{req.metaLabel}</span>
                </p>
              )}
              <p className="text-sm text-gray-600 mt-1">
                Požadované dny:{" "}
                <span className="font-semibold">
                  {formatDaysInterval(req.days)}
                </span>
              </p>
              <p className="text-sm text-gray-600">
                Celkem hodin:{" "}
                <span className="font-semibold">{req.totalHours} h</span>
              </p>
              {req.note && (
                <p className="text-sm text-gray-600 mt-2">
                  Poznámka zaměstnance:{" "}
                  <span className="italic">„{req.note}“</span>
                </p>
              )}
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2 mt-4">
            <div className="space-y-2">
              <h3 className="font-semibold text-sm">
                Přehled obsazenosti pobočky
              </h3>
              <p className="text-xs text-gray-500">
                Zobrazené jsou všechny žádosti této pobočky. Zeleně jsou dny s
                již <strong>schváleným</strong> volnem, žlutě dny s
                <strong> čekající</strong> žádostí. Červená značka = svátek.
              </p>
            </div>

            <div ref={detailCalendarRef} className="border rounded-lg p-2 bg-gray-50">
              {detail.calendarLoading ? (
                <div className="p-4 text-center text-gray-500">
                  Načítám kalendář…
                </div>
              ) : detail.calendarError ? (
                <div className="p-4 text-center text-red-600 font-semibold">
                  {detail.calendarError}
                </div>
              ) : (
                <>
                  <DayPicker
                    locale={cs}
                    weekStartsOn={1}
                    showOutsideDays
                    month={detail.calendarMonth}
                    onMonthChange={(m) =>
                      setDetail((prev) => ({ ...prev, calendarMonth: m }))
                    }
                    modifiers={{
                      today: new Date(),
                      weekend: (date) => date.getDay() === 0 || date.getDay() === 6,
                      holiday: (date) => {
                        const ts = new Date(
                          date.getFullYear(),
                          date.getMonth(),
                          date.getDate()
                        ).getTime();

                        return holidays.some(
                          (h) =>
                            new Date(
                              h.getFullYear(),
                              h.getMonth(),
                              h.getDate()
                            ).getTime() === ts
                        );
                      },
                      approved: detail.approvedDays.map((d) => new Date(d.ts)),
                      pending: detail.pendingDays.map((d) => new Date(d.ts)),
                    }}
                    modifiersClassNames={{
                      today: "day-today",
                      weekend: "day-weekend",
                      holiday: "day-holiday",
                      approved: "day-approved",
                      pending: "day-pending",
                    }}
                  />
                </>
              )}
            </div>
          </div>

          {detail.hoverInfo && (
            <div
              className="fixed z-[999] bg-white border border-gray-300 shadow-lg rounded p-2 text-[12px] w-64"
              style={{ top: detail.hoverInfo.y, left: detail.hoverInfo.x }}
            >
              <div className="font-semibold text-[13px] mb-2">
                {detail.hoverInfo.date.toLocaleDateString("cs-CZ")}
              </div>

              {detail.hoverInfo.pending.length > 0 && (
                <div className="mb-2">
                  <div className="text-yellow-700 font-semibold text-[12px] mb-1">
                    Čekající
                  </div>

                  {detail.hoverInfo.pending.map((p, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-2 text-yellow-700 leading-tight"
                    >
                      <div className="w-2 h-2 bg-yellow-500 rounded-full shrink-0" />
                      <span className="truncate">
                        {p.employeeName} – {formatLeaveType(p.type)}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {detail.hoverInfo.approved.length > 0 && (
                <div>
                  <div className="text-green-700 font-semibold text-[12px] mb-1">
                    Schválené
                  </div>

                  {detail.hoverInfo.approved.map((a, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-2 text-green-700 leading-tight"
                    >
                      <div className="w-2 h-2 bg-green-500 rounded-full shrink-0" />
                      <span className="truncate">
                        {a.employeeName} – {formatLeaveType(a.type)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="flex flex-wrap justify-end gap-4 pt-3 border-t mt-2">
            <button
              className="text-gray-600 hover:text-gray-800 inline-flex items-center gap-1 text-sm"
              onClick={closeDetail}
            >
              <X size={16} /> Zavřít
            </button>

            <button
              className="text-green-600 hover:text-green-800 inline-flex items-center gap-1 text-sm"
              onClick={() => {
                closeDetail();
                approve(req._id);
              }}
            >
              <CheckCircle size={16} /> Schválit
            </button>

            <button
              className="text-yellow-600 hover:text-yellow-800 inline-flex items-center gap-1 text-sm"
              onClick={() => {
                closeDetail();
                openReturn(req._id);
              }}
            >
              <RotateCcw size={16} /> Vrátit
            </button>

            <button
              className="text-red-600 hover:text-red-800 inline-flex items-center gap-1 text-sm"
              onClick={() => {
                closeDetail();
                openReject(req._id);
              }}
            >
              <XCircle size={16} /> Zamítnout
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ------------------------------------------------------------
     UI
  ------------------------------------------------------------ */
  if (authLoading) return <div className="p-6">Načítám…</div>;

  if (!isAdmin && !isRM) {
    return <div className="p-6">Nemáte oprávnění zobrazit tuto stránku.</div>;
  }

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto">
      {renderReasonModal()}
      {renderDetailModal()}

      <h1 className="text-2xl font-bold mb-4">Schvalování žádostí o volno</h1>

      {loading ? (
        <div>Načítám žádosti…</div>
      ) : (
        <table className="w-full border text-center text-sm">
          <thead>
            <tr className="bg-gray-100">
              <th className="border p-2">Zaměstnanec</th>
              <th className="border p-2">Pobočka</th>
              <th className="border p-2">Dny</th>
              <th className="border p-2">Hodin</th>
              <th className="border p-2">Typ</th>
              <th className="border p-2">Poznámka</th>
              <th className="border p-2">Stav</th>
              <th className="border p-2">Akce</th>
            </tr>
          </thead>

          <tbody>
            {requests.length === 0 && (
              <tr>
                <td colSpan={8} className="p-4 text-gray-500">
                  Žádné žádosti.
                </td>
              </tr>
            )}

            {requests.map((req) => (
              <tr key={req._id}>
                <td className="border p-2">{req.employeeName}</td>
                <td className="border p-2">
                  {formatPharmacy(req.pharmacyCode)}
                </td>

                <td className="border p-2">{formatDaysInterval(req.days)}</td>

                <td className="border p-2">{req.totalHours}</td>

                <td className="border p-2 whitespace-pre-wrap">
                  {req.metaLabel || "-"}
                </td>

                <td className="border p-2 whitespace-pre-wrap">
                  {req.note || "-"}
                </td>

                <td
                  className="border p-2 font-semibold"
                  style={{ color: statusColor(req.status) }}
                >
                  {req.status}
                </td>

                <td className="border p-2">
                  <div className="flex flex-wrap justify-center gap-3">
                    <button
                      className="text-blue-600 hover:text-blue-800 inline-flex items-center gap-1 text-sm"
                      onClick={() => openDetail(req)}
                    >
                      <Eye size={16} /> Zobrazit
                    </button>

                    {req.status === "čeká" && (
                      <>
                        <button
                          className="text-green-600 hover:text-green-800 inline-flex items-center gap-1 text-sm"
                          onClick={() => approve(req._id)}
                        >
                          <CheckCircle size={16} /> Schválit
                        </button>

                        <button
                          className="text-yellow-600 hover:text-yellow-800 inline-flex items-center gap-1 text-sm"
                          onClick={() => openReturn(req._id)}
                        >
                          <RotateCcw size={16} /> Vrátit
                        </button>

                        <button
                          className="text-red-600 hover:text-red-800 inline-flex items-center gap-1 text-sm"
                          onClick={() => openReject(req._id)}
                        >
                          <XCircle size={16} /> Zamítnout
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}